import { TravelPackage } from '../types/TravelPackage';
import { getApiHeaders } from '../utils/apiHeaders';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const getAiBaseUrl = () => process.env.REACT_APP_AI_BACKEND_BASE_URL;

export type PackageContentGenerationInput = {
  packageName: string;
  places: Array<{
    cityName: string;
    cityCode?: string;
    nights: number;
  }>;
  arrivalAt: string;
  departureFrom: string;
  tone?: string;
  market?: string;
};

export type PackageContentGenerationResult = {
  title?: string;
  subtitle?: string;
  description?: string;
  highlights?: string[];
  itinerary?: Array<{
    day: number;
    title: string;
    points: string[];
  }>;
  inclusions?: string[];
  exclusions?: string[];
  faq?: Array<{
    question: string;
    answer: string;
  }>;
  searchTags?: string[];
  requestId?: string;
  provider?: string;
  model?: string;
  generatedFields?: string[];
};

export type DayItineraryRewrite = { day: number; title: string; points: string[]; generated: boolean };
export async function rewriteDayItinerary(input: { day: number; theme?: string; activities: Array<{ name: string }> }): Promise<DayItineraryRewrite> {
  const response = await fetch(`${API_BASE_URL}/api/packages/ai/rewrite-day`, { method: 'POST', headers: getApiHeaders('json'), body: JSON.stringify(input) });
  if (!response.ok) throw new Error('Failed to generate itinerary from activities');
  return response.json();
}

export type WebsiteV2JourneyDraftInput = {
  title: string;
  destinationSlugs: string[];
  durationLabel?: string;
  routeLabel?: string;
  styleTags?: string[];
  audience?: string;
  roughDraft?: string;
};

export type WebsiteV2JourneyDraftResult = {
  requestId?: string;
  provider?: string;
  model?: string;
  generatedFields?: string[];
  draft: {
    slug?: string;
    title?: string;
    summary?: string;
    durationLabel?: string;
    routeLabel?: string;
    destinationSlugs?: string[];
    primaryDestinationSlug?: string;
    styleTags?: string[];
    bestFor?: string[];
    highlights?: string[];
    faq?: Array<{
      question: string;
      answer: string;
    }>;
    itinerary?: Array<{
      day: number;
      title: string;
      description: string | string[];
    }>;
    includedExperiences?: string[];
    stayStyle?: string;
    seo?: {
      title?: string;
      description?: string;
    };
  };
};

export const createPackage = async (formData: FormData): Promise<TravelPackage> => {
  const response = await fetch(`${API_BASE_URL}/api/packages`, {
    method: 'POST',
    headers: getApiHeaders('form-data'),
    body: formData,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || `Failed to create package: ${response.statusText}`);
  }

  const data = await response.json();
  return data;
};

export const fetchPackages = async (): Promise<TravelPackage[]> => {
  const response = await fetch(`${API_BASE_URL}/api/packages`, {
      headers: getApiHeaders('json')
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch packages: ${response.statusText}`);
  }

  const data = await response.json();
  return data;
};

export const searchPackages = async (search: string): Promise<TravelPackage[]> => {
  const url = new URL(`${API_BASE_URL}/api/packages`);
  url.searchParams.set('search', search);
  const response = await fetch(url.toString(), {
    headers: getApiHeaders('json'),
  });

  if (!response.ok) {
    throw new Error(`Failed to search packages: ${response.statusText}`);
  }

  const data = await response.json();
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.packages)) return data.packages;
  if (Array.isArray(data?.items)) return data.items;
  return [];
};

export type PackagesPagination = {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
};

export type PackagesPageResponse = {
  items: TravelPackage[];
  pagination: PackagesPagination;
};

const normalizePackagesResponse = (
  data: any,
  fallbackPage: number,
  fallbackPageSize: number
): PackagesPageResponse => {
  const items: TravelPackage[] = Array.isArray(data)
    ? data
    : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.packages)
          ? data.packages
          : Array.isArray(data?.results)
            ? data.results
            : [];

  const page = Number(data?.page ?? data?.pagination?.page ?? fallbackPage);
  const pageSize = Number(
    data?.pageSize ??
    data?.limit ??
    data?.pagination?.pageSize ??
    data?.pagination?.limit ??
    fallbackPageSize
  );
  const total = Number(
    data?.total ??
    data?.count ??
    data?.totalCount ??
    data?.pagination?.total ??
    data?.pagination?.count ??
    items.length
  );
  const totalPages = Number(
    data?.totalPages ??
    data?.pages ??
    data?.pagination?.totalPages ??
    Math.max(1, Math.ceil(total / (pageSize || 1)))
  );

  return {
    items,
    pagination: {
      page: Number.isFinite(page) && page > 0 ? page : fallbackPage,
      pageSize: Number.isFinite(pageSize) && pageSize > 0 ? pageSize : fallbackPageSize,
      total: Number.isFinite(total) && total >= 0 ? total : items.length,
      totalPages: Number.isFinite(totalPages) && totalPages > 0 ? totalPages : 1,
    },
  };
};

export const fetchPackagesPage = async (params?: {
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<PackagesPageResponse> => {
  const url = new URL(`${API_BASE_URL}/api/packages`);
  const page = params?.page ?? 1;
  const pageSize = params?.pageSize ?? 20;

  if (params?.search) url.searchParams.set('search', params.search);
  url.searchParams.set('page', String(page));
  url.searchParams.set('pageSize', String(pageSize));

  const response = await fetch(url.toString(), {
    headers: getApiHeaders('json')
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch packages: ${response.statusText}`);
  }

  const data = await response.json();
  return normalizePackagesResponse(data, page, pageSize);
};

export const updatePackage = async (id: string, formData: FormData): Promise<TravelPackage> => {
  const response = await fetch(`${API_BASE_URL}/api/packages/${id}`, {
    method: 'PUT',
    headers: getApiHeaders('form-data'),
    body: formData,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || `Failed to update package: ${response.statusText}`);
  }

  const data = await response.json();
  return data;
};

export const deletePackage = async (id: string): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/packages/${id}`, {
    method: 'DELETE',
    headers: getApiHeaders('none'),
  });

  if (!response.ok) {
    throw new Error(`Failed to delete package: ${response.statusText}`);
  }
};

export const fetchPackageById = async (id: string): Promise<TravelPackage> => {
  const response = await fetch(`${API_BASE_URL}/api/packages/${id}`, {
    headers: getApiHeaders('json')
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch package: ${response.statusText}`);
  }

  const data = await response.json();
  return data;
};

export const generateWebsiteV2JourneyDraft = async (
  payload: WebsiteV2JourneyDraftInput
): Promise<WebsiteV2JourneyDraftResult> => {
  const aiBaseUrl = getAiBaseUrl();
  if (!aiBaseUrl) {
    throw new Error(
      [
        'Missing REACT_APP_AI_BACKEND_BASE_URL.',
        'Create query-app/.env and restart the dev server.',
        'Example: REACT_APP_AI_BACKEND_BASE_URL=http://localhost:3004',
      ].join(' ')
    );
  }

  const endpoint = `${aiBaseUrl.replace(/\/$/, '')}/website-v2/journeys/ai-draft`;
  let response: Response;

  try {
    response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        ...getApiHeaders('json'),
        'X-Client-Platform': 'query-app',
      },
      body: JSON.stringify(payload),
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown network error';
    throw new Error(
      `Unable to reach Website V2 journey draft API at ${endpoint}. ` +
      `Check that ai-backend is running and that CORS allows the query-app origin. ` +
      `Underlying error: ${message}`
    );
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || 'Failed to generate Website V2 journey draft');
  }

  const data = await response.json();
  return {
    requestId: data?.requestId,
    provider: data?.metadata?.provider,
    model: data?.metadata?.model,
    generatedFields: data?.metadata?.generatedFields,
    draft: data?.draft || {},
  };
};
